import { ChangeDetectorRef, Component, inject, input } from '@angular/core';
import { NaturalLanguageEditorKeyphraseAbstractDirective } from '../natural-language-editor-keyphrase.abstract.directive';
import { Observable, Subject, switchMap, timer } from 'rxjs';
import { debounce, filter, tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { ZiroIcons } from '../../../models/ziro-icons.enum';

export type NaturalLanguageDoubleEntity = { primary: string; secondary: string };

export interface NaturalLanguageEditorSecondaryValidator {
  validator: (value: NaturalLanguageDoubleEntity) => boolean;
  message: string;
}

export interface NaturalLanguageEditorDoublePrimaryDropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
  secondaryInputConfiguration:
    | NaturalLanguageEditorDoubleSecondaryNullConfiguration
    | NaturalLanguageEditorDoubleSecondaryTextConfiguration
    | NaturalLanguageEditorDoubleSecondarySearchConfiguration;
}

export interface NaturalLanguageEditorDoubleSecondaryNullConfiguration {
  type: 'none';
  validation?: NaturalLanguageEditorSecondaryValidator[];
}

export interface NaturalLanguageEditorDoubleSecondaryTextConfiguration {
  type: 'text';
  label: string;
  placeholder: string;
  validation?: NaturalLanguageEditorSecondaryValidator[];
}

export interface NaturalLanguageEditorDoubleSecondarySearchConfiguration {
  type: 'search';
  label: string;
  placeholder: string;
  searchFn: (val: string) => Observable<string[]>;
  validation?: NaturalLanguageEditorSecondaryValidator[];
  minSearchLength?: number;
}

@Component({
  selector: 'ziro-natural-language-editor-double',
  templateUrl: 'natural-language-editor-double.component.html',
  styleUrls: ['../natural-language-editor-keyphrase.component.scss'],
  providers: [
    { provide: NaturalLanguageEditorKeyphraseAbstractDirective, useExisting: NaturalLanguageEditorDoubleComponent },
  ],
  standalone: false,
})
export class NaturalLanguageEditorDoubleComponent extends NaturalLanguageEditorKeyphraseAbstractDirective<NaturalLanguageDoubleEntity> {
  private changeDetectorRef = inject(ChangeDetectorRef);

  readonly options = input<NaturalLanguageEditorDoublePrimaryDropdownOption[]>();
  readonly primaryDropdownLabel = input<string>();

  primaryDropdownValue: NaturalLanguageEditorDoublePrimaryDropdownOption;
  entityPrimaryDropdownValue: NaturalLanguageEditorDoublePrimaryDropdownOption;
  secondaryInputValue = '';
  typeahead$: Subject<string>;
  searchOptions$: Observable<string[]>;
  isSearching = false;
  validationMessage = '';

  ziroIcons = ZiroIcons;

  onUpdate(update: NaturalLanguageDoubleEntity): void {
    this.primaryDropdownValue = this.options().find((opt) => opt.value === update.primary);
    if (this.primaryDropdownValue?.secondaryInputConfiguration.type === 'search') {
      this._setAsyncOptions(this.primaryDropdownValue.secondaryInputConfiguration.searchFn);
    }
    this.entityPrimaryDropdownValue = cloneDeep(this.primaryDropdownValue);
    this.secondaryInputValue = update.secondary;
    super.onUpdate(update);
    this.changeDetectorRef.detectChanges();
  }

  handlePopoverClose(): void {
    this.onUpdate({
      primary: this.primaryDropdownValue.value,
      secondary: this.secondaryInputValue,
    });
  }

  onPrimaryDropdownChange(): void {
    if (this.primaryDropdownValue?.secondaryInputConfiguration?.type === 'search') {
      this.secondaryInputValue = null;
      this._setAsyncOptions(this.primaryDropdownValue.secondaryInputConfiguration.searchFn);
    } else {
      this.secondaryInputValue = '';
    }
    this.onSecondaryInputChange();
  }

  onSecondaryInputChange(): void {
    this.validationMessage = this._generateFeedback({
      primary: this.primaryDropdownValue?.value,
      secondary: this.secondaryInputValue,
    });
  }

  protected _generateFeedback(update: NaturalLanguageDoubleEntity): string {
    if (this.primaryDropdownValue.secondaryInputConfiguration.validation) {
      for (const validationItem of this.primaryDropdownValue.secondaryInputConfiguration.validation) {
        if (!validationItem.validator(update)) {
          return validationItem.message;
        }
      }
    }
    return '';
  }

  private _setAsyncOptions(searchFn: (term: string) => Observable<string[]>): void {
    this.typeahead$ = new Subject<string>();
    this.searchOptions$ = this.typeahead$.pipe(
      filter((searchTerm) => !!searchTerm),
      debounce(() => timer(500)),
      tap(() => {
        this.isSearching = true;
      }),
      switchMap(searchFn),
      tap(() => {
        this.isSearching = false;
      }),
    );
  }
}
